import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Zoho Books Automation Accounts → Manage users API.
 *
 * A "Zoho user" is a Zoho account the bot makes Books API calls as, authorised
 * via Self Client: the admin generates a grant code in the API console of the
 * account's datacentre (api-console.zoho.in / api-console.zoho.com) and pastes
 * it here; the backend exchanges it immediately (codes die in minutes) and
 * stores the tokens encrypted. Secrets never come back — only flags.
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface ZohoOrganizationDTO {
  organization_id: string;
  name: string;
  currency_code: string | null;
  is_default_org: boolean;
}

export interface ZohoUserDTO {
  public_id: string;
  label: string | null;
  zoho_email: string;
  dc: string;
  auth_type: string;
  client_id: string;
  has_client_secret: boolean;
  has_refresh_token: boolean;
  /** Zoho exposes no API for accepting an org invite, so the agent signs in for
   *  real. The password never comes back — only whether one is stored. */
  has_password: boolean;
  zoho_login_email: string;
  browser_session_state: "unknown" | "logged_in" | "needs_login" | "blocked";
  browser_session_ok_at: string | null;
  api_domain: string | null;
  granted_scopes: string | null;
  /** Books orgs this user can reach — cached from the last successful probe. */
  organizations: ZohoOrganizationDTO[];
  status: "connected" | "disconnected" | "error";
  last_error: string | null;
  last_verified_at: string | null;
  last_used_at: string | null;
  token_expires_at: string | null;
  tenant_count: number;
  max_tenants: number;
  created_at: string | null;
}

export interface ZohoUserTenantDTO {
  tenant_id: number;
  company_name: string | null;
  zoho_organization_id: string | null;
  assigned_at: string | null;
}

export interface AddZohoUserInput {
  label?: string;
  zoho_email: string;
  /**
   * Zoho datacentre the account lives on: "in" (India, the default) or "com"
   * (US). Datacentres are isolated — a bot can only be invited into
   * organisations on its own DC. Anything unknown is refused (422).
   */
  dc?: string;
  client_id: string;
  client_secret: string;
  /** Self Client grant code — single-use, expires 3–10 min after generation. */
  grant_code: string;
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export type ZohoUsersResponseDTO = Envelope<{ users: ZohoUserDTO[] }>;
export type ZohoUserResponseDTO = Envelope<{ user: ZohoUserDTO }>;
export type ZohoUserDetailResponseDTO = Envelope<{
  user: ZohoUserDTO;
  tenants: ZohoUserTenantDTO[];
}>;
export type ZohoTestResponseDTO = Envelope<{
  organizations: ZohoOrganizationDTO[];
  user: ZohoUserDTO;
}>;
export type ZohoTenantsResponseDTO = Envelope<{ tenants: ZohoUserTenantDTO[] }>;

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      // Validation errors carry the useful text per-field — surface it.
      const fieldMessage =
        payload?.errors && typeof payload.errors === "object"
          ? Object.values(payload.errors as Record<string, string>)[0]
          : null;
      throw new APIError(
        fieldMessage || payload.message || `HTTP error! status: ${response.status}`,
        response.status,
        payload
      );
    }

    return payload as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
}

export const listZohoUsers = async (): Promise<ZohoUsersResponseDTO> =>
  postJson<ZohoUsersResponseDTO>("/api/bot/zoho/users/list", {});

export const addZohoUser = async (input: AddZohoUserInput): Promise<ZohoUserResponseDTO> =>
  postJson<ZohoUserResponseDTO>("/api/bot/zoho/users/add", input);

export const getZohoUser = async (publicId: string): Promise<ZohoUserDetailResponseDTO> =>
  postJson<ZohoUserDetailResponseDTO>("/api/bot/zoho/users/get", { public_id: publicId });

export const updateZohoUser = async (input: {
  public_id: string;
  label?: string | null;
  zoho_login_email?: string;
  /** Blank keeps the stored password. It is never sent back to the client. */
  zoho_password?: string;
}): Promise<ZohoUserResponseDTO> =>
  postJson<ZohoUserResponseDTO>("/api/bot/zoho/users/update", input);

export const reconnectZohoUser = async (input: {
  public_id: string;
  grant_code: string;
  /** Blank keeps the stored credentials. */
  client_id?: string;
  client_secret?: string;
}): Promise<ZohoUserResponseDTO> =>
  postJson<ZohoUserResponseDTO>("/api/bot/zoho/users/reconnect", input);

export const removeZohoUser = async (publicId: string): Promise<Envelope<{}>> =>
  postJson<Envelope<{}>>("/api/bot/zoho/users/remove", { public_id: publicId });

export const testZohoUser = async (publicId: string): Promise<ZohoTestResponseDTO> =>
  postJson<ZohoTestResponseDTO>("/api/bot/zoho/users/test", { public_id: publicId });

export const assignZohoTenant = async (input: {
  public_id: string;
  tenant_id: number;
  zoho_organization_id?: string;
}): Promise<ZohoTenantsResponseDTO> =>
  postJson<ZohoTenantsResponseDTO>("/api/bot/zoho/users/tenants/assign", input);

export const unassignZohoTenant = async (input: {
  public_id: string;
  tenant_id: number;
}): Promise<ZohoTenantsResponseDTO> =>
  postJson<ZohoTenantsResponseDTO>("/api/bot/zoho/users/tenants/unassign", input);

export { APIError as BotZohoAPIError };
